/*
  Devuchi #221 / Trello 1234 — CTA buttons.
  Devuchi #224 / Trello 1239 — image glitch.

  Inline size dropdown on product cards. Changing the size updates:
    1. the hidden input[name="id"] the existing <product-form> submits,
    2. the price printed inside the "Add to Bag" button, and
    3. the card image, swapped to the selected variant's own image.

  The card normally shows a curated `custom.card_image` metafield rather than a
  variant image, so the original is kept in data-default-src and restored for any
  variant that has no image of its own (e.g. Gift Card).

  Styling is intentionally untouched — the client asked for this functionality
  "without changing our rounded buttons".

  No dependency on cart.js: <product-form> already reads input[name="id"] at
  submit time, so updating that input is all the add-to-cart flow needs.
*/

/*
  Devuchi #224 R3 — fitting a square variant image into the 4:5 card box.

  Every variant image in the catalog is a square 1:1 hero shot while the card box is
  pinned to aspect-ratio 4/5, so object-fit:cover removed 20% off each side and
  sliced the Allure award emblem baked into the photo's top-right corner.

  R2 solved that with object-fit:contain plus a blurred copy of the image behind it.
  The client rejected the look: "the images are showing up with a weird blur
  background behind it?" — on the purple heroes the blur dragged the emblem's red
  into the fill and left a visible seam.

  R3 does it properly and without any blur: Shopify's image CDN pads server-side.
  Asking for `&width=W&height=H&pad_color=RRGGBB` returns the WHOLE image letterboxed
  into that box with flat bars in the given colour, nothing cropped. The bar colour is
  sampled from the image's own outermost pixel row, so it continues the photo's
  backdrop exactly. Measured spread within a single edge row is <= 7/255 on every
  catalog hero, while top and bottom differ by ~10 on the purple ones — so the top and
  bottom bars are sampled and requested separately rather than sharing one colour.

  The result is a plain 4:5 image the card can render with ordinary object-fit:cover:
  no contain, no blur layer, no CSS special case.

  Sampling needs canvas pixel access. The Shopify CDN sends
  `access-control-allow-origin: *`, so a crossOrigin="anonymous" probe reads clean.
  If anything in that path fails (canvas tainted, probe blocked, CDN ignores the
  parameters) the code falls back to contain-without-blur, which is still uncropped.
*/
const DL_CARD_BOX_RATIO = 4 / 5;   // the .variant-selected box, width / height
const dlPaddedUrlCache = {};

function dlToHex(rgb) {
  return rgb.map(function (v) {
    const s = Math.max(0, Math.min(255, v)).toString(16);
    return s.length === 1 ? '0' + s : s;
  }).join('');
}

// Average an edge row, ignoring nothing: these heroes are flat backdrops, and the
// product/emblem never touches the very outermost row (verified across the catalog).
function dlAverageRow(data, width, y) {
  let r = 0, g = 0, b = 0, n = 0;
  for (let x = 0; x < width; x += 2) {
    const i = (y * width + x) * 4;
    r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
  }
  return [Math.round(r / n), Math.round(g / n), Math.round(b / n)];
}

/*
  Resolve `src` to a CDN url already padded to 4:5, or null if that is not possible.
  Returns a promise so the caller can swap in one finished image rather than showing
  an unpadded frame first.
*/
function dlPaddedCardImage(src) {
  if (dlPaddedUrlCache[src] !== undefined) {
    return Promise.resolve(dlPaddedUrlCache[src]);
  }
  return new Promise(function (resolve) {
    let settled = false;
    const done = function (value) {
      if (settled) return;
      settled = true;
      dlPaddedUrlCache[src] = value;
      resolve(value);
    };

    // Only Shopify CDN urls accept the sizing parameters.
    if (src.indexOf('/cdn/shop/') === -1) return done(null);

    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    // A small probe is enough to average an edge row and keeps the request cheap.
    probe.src = src.indexOf('?') === -1 ? src + '?width=200' : src.replace(/([?&])width=\d+/, '$1width=200');
    if (probe.src.indexOf('width=') === -1) probe.src = probe.src + '&width=200';

    const bail = setTimeout(function () { done(null); }, 4000);

    probe.onerror = function () { clearTimeout(bail); done(null); };
    probe.onload = function () {
      clearTimeout(bail);
      const w = probe.naturalWidth;
      const h = probe.naturalHeight;
      // Already 4:5 or taller: cover() crops nothing, so leave it alone.
      if (!w || !h || w / h <= DL_CARD_BOX_RATIO + 0.01) return done(null);

      let top, bottom;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(probe, 0, 0);
        const px = ctx.getImageData(0, 0, w, h).data;
        top = dlAverageRow(px, w, 0);
        bottom = dlAverageRow(px, w, h - 1);
      } catch (e) {
        return done(null);   // tainted canvas — fall back to contain
      }

      // Request the padded render at the card's real display size (2x for retina).
      const outW = 800;
      const outH = Math.round(outW / DL_CARD_BOX_RATIO);   // 1000
      const base = src.split('?')[0];
      const version = (src.match(/[?&](v=\d+)/) || [])[1];
      const q = function (padHex) {
        return base + '?' + (version ? version + '&' : '') +
               'width=' + outW + '&height=' + outH + '&pad_color=' + padHex;
      };

      if (dlToHex(top) === dlToHex(bottom)) {
        // One flat backdrop — a single padded render is exact.
        return done({ single: q(dlToHex(top)) });
      }
      // Top and bottom differ (gradient backdrop). Pad using the TOP colour, then
      // repaint just the bottom bar with its own colour. Each bar is flat, so a plain
      // colour is exact there and costs no extra image request.
      done({
        single: q(dlToHex(top)),
        bottomColor: 'rgb(' + bottom.join(',') + ')'
      });
    };
  });
}

class CardVariantSelect extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    if (!this.select) return;

    this.form = this.closest('form');
    this.idInput = this.form && this.form.querySelector('input[name="id"]');
    this.button = this.form && this.form.querySelector('button[type="submit"]');

    const card = this.closest('.flex.flex-col');
    this.image = card && card.querySelector('img.dl-card-image');
    this.wrap = this.image && this.image.closest('.dl-image-badge-wrap');

    this.select.addEventListener('change', () => this.onChange(true));
    // Sync price/id/disabled on load. The image is NOT swapped on load: the card is
    // meant to show the curated custom.card_image in the grid, and only follow the
    // dropdown once the shopper actually picks a size.
    this.onChange(false);
  }

  onChange(swapImage) {
    const option = this.select.options[this.select.selectedIndex];
    if (!option) return;

    if (this.idInput) this.idInput.value = option.value;
    if (swapImage) this.swapImage(option);
    if (!this.button) return;

    const available = option.dataset.available === 'true';
    const priceEl = this.button.querySelector('.dl-card-cta-price');
    const compareEl = this.button.querySelector('.dl-card-cta-compare');

    if (priceEl) priceEl.textContent = available ? ' - ' + option.dataset.price : '';

    // Devuchi #224 R3 — the struck-through "was" price follows the size too, and
    // disappears on any variant that is not discounted.
    if (compareEl) {
      const compare = option.dataset.compare || '';
      compareEl.textContent = available && compare ? ' ' + compare : '';
      compareEl.hidden = !(available && compare);
    }

    if (available) {
      this.button.removeAttribute('disabled');
    } else {
      this.button.setAttribute('disabled', 'disabled');
    }
  }

  swapImage(option) {
    if (!this.image) return;

    const next = option.dataset.image || this.image.dataset.defaultSrc;
    if (!next || this.image.dataset.dlCurrent === next) return;
    this.image.dataset.dlCurrent = next;

    // srcset/sizes would otherwise win over the new src and keep the old image up.
    this.image.removeAttribute('srcset');
    this.image.removeAttribute('sizes');

    const token = (this.swapToken = (this.swapToken || 0) + 1);

    dlPaddedCardImage(next).then((padded) => {
      if (token !== this.swapToken) return;   // a newer selection won the race

      if (!padded) {
        // No padded render available. Show the whole image rather than cropping it.
        this.image.setAttribute('src', next);
        this.applyContainFallback();
        return;
      }

      this.clearFit();
      this.image.setAttribute('src', padded.single);
      if (padded.bottomColor) {
        this.wrap.style.setProperty('--dl-card-pad-bottom', padded.bottomColor);
        this.wrap.setAttribute('data-dl-pad', 'split');
      }
    });
  }

  applyContainFallback() {
    if (!this.wrap) return;
    const decide = () => {
      const w = this.image.naturalWidth;
      const h = this.image.naturalHeight;
      if (!w || !h) return;
      this.clearFit();
      if (w / h > DL_CARD_BOX_RATIO + 0.01) this.wrap.setAttribute('data-dl-fit', 'contain');
    };
    if (this.image.complete && this.image.naturalWidth) {
      decide();
    } else {
      this.image.addEventListener('load', decide, { once: true });
    }
  }

  clearFit() {
    if (!this.wrap) return;
    this.wrap.removeAttribute('data-dl-fit');
    this.wrap.removeAttribute('data-dl-pad');
    this.wrap.style.removeProperty('--dl-card-pad-bottom');
    this.wrap.style.removeProperty('--dl-card-fill');
  }
}

customElements.define('card-variant-select', CardVariantSelect);
